import React from 'react'
import { useMediaQuery } from 'react-responsive'

type Variant = 'intrinsic' | 'fill'

type Props = {
  alt: string
  posterSrc?: string
  previewSrcUrl?: string
  className?: string
  variant?: Variant
  cardHovered?: boolean
}

/**
 * Poster thumbnail with optional `preview.webp` on hover
 */
const VideoCardPreviewMedia: React.FC<Props> = ({
  alt,
  posterSrc,
  previewSrcUrl,
  className = '',
  variant = 'intrinsic',
  cardHovered,
}) => {
  const prefersHover = useMediaQuery({ query: '(hover: hover)' })
  const [mediaHovered, setMediaHovered] = React.useState(false)
  const controlledByCard = cardHovered !== undefined

  const previewUrl = previewSrcUrl

  const usePreviewOnHover = Boolean(previewUrl && prefersHover)
  const isFill = variant === 'fill'
  const showPreview = controlledByCard ? Boolean(cardHovered) : mediaHovered

  if (!usePreviewOnHover) {
    return (
      <img
        src={posterSrc}
        alt={alt}
        className={isFill ? `absolute inset-0 h-full w-full object-cover ${className}` : className}
      />
    )
  }

  const fillImg = 'absolute inset-0 h-full w-full object-cover'

  return (
    <div
      className={isFill ? 'absolute inset-0 overflow-hidden' : 'relative overflow-hidden'}
      onMouseEnter={!controlledByCard ? () => setMediaHovered(true) : undefined}
      onMouseLeave={!controlledByCard ? () => setMediaHovered(false) : undefined}
    >
      <img src={posterSrc} alt={alt} className={isFill ? `${fillImg} ${className}` : className} />
      <img
        src={previewUrl ?? undefined}
        alt=''
        role='presentation'
        loading='lazy'
        decoding='async'
        className={`${isFill ? fillImg : `absolute inset-0 h-full w-full object-cover ${className}`} transition-opacity duration-200 ${
          showPreview ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  )
}

export default VideoCardPreviewMedia
